import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, Save, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TIERS = ['MINI', 'MINOR', 'MAJOR', 'MEGA'];
const TIER_COLOR = { MINI: '#3ad07a', MINOR: '#3aa0ff', MAJOR: '#b06bff', MEGA: '#ffd24a' };

export const BofJackpotsAdminTab = () => {
    const [data, setData] = useState(null);
    const [edited, setEdited] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data: cfg } = await axios.get(`${API}/admin/bof-jackpots`, { withCredentials: true });
            setData(cfg);
            setEdited(JSON.parse(JSON.stringify(cfg)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Balls of Fire jackpots');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const seeds = {}, contrib_rates = {};
            TIERS.forEach((t) => {
                seeds[t] = parseFloat(edited.tiers[t].seed);
                contrib_rates[t] = parseFloat(edited.tiers[t].contrib_rate);
            });
            await axios.put(`${API}/admin/bof-jackpots`,
                { seeds, contrib_rates, mega_min_bet: parseInt(edited.mega_min_bet) },
                { withCredentials: true });
            toast.success('Balls of Fire jackpots saved');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const resetTier = async (tier) => {
        if (!window.confirm(`Reset the ${tier} pool back to its seed now?`)) return;
        try {
            await axios.post(`${API}/admin/bof-jackpots/reset`, { tier }, { withCredentials: true });
            toast.success(`${tier} pool reset to seed`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
    };

    if (!data || !edited) {
        return <div className="text-[#A0AAB2] py-12 text-center">Loading Balls of Fire jackpots…</div>;
    }

    return (
        <div className="card-dark p-5 space-y-4" data-testid="bof-jackpots-admin">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-[#ff8a1e]" />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">Balls of Fire — Progressive Jackpots</h3>
                </div>
                <Button size="sm" variant="ghost" onClick={load} className="text-[#A0AAB2] h-8 text-xs" data-testid="bof-jp-refresh">
                    <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                </Button>
            </div>
            <p className="text-[11px] text-[#A0AAB2]">
                Shared pools shown to every player. They grow as people play Balls of Fire (house-funded — never taken from player wagers).
                A hit pays the current pool then resets to its seed. Amounts are in credits (same units shown on the game's jackpot strip).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TIERS.map((t) => (
                    <div key={t} className="p-4 rounded-lg border" style={{ borderColor: TIER_COLOR[t] + '55', background: TIER_COLOR[t] + '0d' }} data-testid={`bof-jp-tier-${t.toLowerCase()}`}>
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-black text-sm uppercase tracking-widest" style={{ color: TIER_COLOR[t] }}>{t}</span>
                            <div className="text-right">
                                <div className="text-[10px] text-[#A0AAB2] uppercase">Live pool</div>
                                <div className="font-mono font-black text-lg" style={{ color: TIER_COLOR[t] }} data-testid={`bof-jp-live-${t.toLowerCase()}`}>
                                    {Math.round(data.tiers[t].current).toLocaleString()}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-white text-xs">Seed (start / reset)</Label>
                                <Input type="number" step="1" min="0"
                                    value={edited.tiers[t].seed}
                                    onChange={(e) => setEdited({ ...edited, tiers: { ...edited.tiers, [t]: { ...edited.tiers[t], seed: e.target.value } } })}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid={`bof-jp-seed-${t.toLowerCase()}`} />
                            </div>
                            <div>
                                <Label className="text-white text-xs">Growth rate (× bet / spin)</Label>
                                <Input type="number" step="0.0005" min="0"
                                    value={edited.tiers[t].contrib_rate}
                                    onChange={(e) => setEdited({ ...edited, tiers: { ...edited.tiers, [t]: { ...edited.tiers[t], contrib_rate: e.target.value } } })}
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid={`bof-jp-rate-${t.toLowerCase()}`} />
                            </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => resetTier(t)}
                            className="mt-3 h-7 text-xs border-white/15 text-[#A0AAB2] hover:bg-white/5"
                            data-testid={`bof-jp-reset-${t.toLowerCase()}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Reset pool to seed
                        </Button>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-white/5">
                <div>
                    <Label className="text-white text-xs">MEGA minimum bet to win the pool</Label>
                    <Input type="number" step="1" min="0"
                        value={edited.mega_min_bet}
                        onChange={(e) => setEdited({ ...edited, mega_min_bet: e.target.value })}
                        className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                        data-testid="bof-jp-mega-minbet" />
                    <p className="text-[9px] text-[#A0AAB2] mt-1">Below this bet, a MEGA hit pays only the MEGA seed and the pool keeps growing. Other tiers: any bet wins.</p>
                </div>
            </div>

            <Button onClick={save} disabled={saving || loading}
                className="bg-[#ff8a1e] text-black hover:bg-[#ff8a1e]/90 font-bold"
                data-testid="bof-jp-save">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Jackpots'}
            </Button>
        </div>
    );
};

export default BofJackpotsAdminTab;
